/**
 * Desktop window geometry — pure functions, no React.
 *
 * A window is free (its own rect) or TILED, as on macOS: dragged to a screen
 * edge it snaps to that half (the top edge fills the desktop), to a corner it
 * takes that quarter; the green button's menu and the Window menu offer the
 * same, plus "Arrange" (every visible window side by side). A tiled window
 * remembers the free rect it came from ("Return to previous size", or simply
 * dragging it away).
 *
 * All rects are in the desktop's own coordinates (its top-left is 0,0) and the
 * work area is the desktop less the Dock.
 */

export type TileKind = 'fill' | 'left' | 'right' | 'top' | 'bottom' | 'tl' | 'tr' | 'bl' | 'br'
export interface Rect { x: number; y: number; w: number; h: number }

export interface WinState {
  rect: Rect
  tile: TileKind | null
  /** The free rect a tiled window returns to. */
  restore: Rect | null
  /** In the Dock, not on the desktop. */
  min: boolean
  /** Stacking order — higher is in front. */
  z: number
}

/** Space between a window and the work area's edge / its tiled neighbour. */
export const GAP = 6
export const MIN_W = 380
export const MIN_H = 260
/** How close to an edge (px) a dragged pointer must come to offer a tile. */
export const EDGE = 22
/** Corner zone: within this of BOTH edges is a quarter. */
const CORNER = 64

export const TILE_LABELS: Record<TileKind, string> = {
  fill: 'Fill', left: 'Left half', right: 'Right half', top: 'Top half', bottom: 'Bottom half',
  tl: 'Top-left quarter', tr: 'Top-right quarter', bl: 'Bottom-left quarter', br: 'Bottom-right quarter',
}

export function tileRect(kind: TileKind, area: Rect, gap = GAP): Rect {
  const x0 = area.x + gap
  const y0 = area.y + gap
  const W = Math.max(0, area.w - 2 * gap)
  const H = Math.max(0, area.h - 2 * gap)
  const hw = (W - gap) / 2
  const hh = (H - gap) / 2
  switch (kind) {
    case 'fill': return { x: x0, y: y0, w: W, h: H }
    case 'left': return { x: x0, y: y0, w: hw, h: H }
    case 'right': return { x: x0 + hw + gap, y: y0, w: hw, h: H }
    case 'top': return { x: x0, y: y0, w: W, h: hh }
    case 'bottom': return { x: x0, y: y0 + hh + gap, w: W, h: hh }
    case 'tl': return { x: x0, y: y0, w: hw, h: hh }
    case 'tr': return { x: x0 + hw + gap, y: y0, w: hw, h: hh }
    case 'bl': return { x: x0, y: y0 + hh + gap, w: hw, h: hh }
    case 'br': return { x: x0 + hw + gap, y: y0 + hh + gap, w: hw, h: hh }
    default: return { x: x0, y: y0, w: W, h: H }
  }
}

/**
 * Where a newly opened window goes. The first fills the work area (with just
 * the small gap — a screen is the point of the desktop); later ones cascade at
 * three quarters of the area so the one behind stays reachable.
 */
export function defaultRect(area: Rect, index: number): Rect {
  if (index <= 0) return tileRect('fill', area)
  const w = Math.max(MIN_W, Math.round(area.w * 0.74))
  const h = Math.max(MIN_H, Math.round(area.h * 0.78))
  const step = 28 * (index % 6)
  return clampRect({ x: area.x + GAP + 24 + step, y: area.y + GAP + 18 + step, w, h }, area)
}

/** Keep a window usable: never smaller than the minimum, never bigger than the
 *  area, and its title bar always on the desktop so it can be dragged back. */
export function clampRect(r: Rect, area: Rect): Rect {
  const w = Math.min(Math.max(MIN_W, r.w), Math.max(MIN_W, area.w))
  const h = Math.min(Math.max(MIN_H, r.h), Math.max(MIN_H, area.h))
  const x = Math.min(Math.max(r.x, area.x - w + 140), area.x + area.w - 140)
  const y = Math.min(Math.max(r.y, area.y), area.y + area.h - 44)
  return { x, y, w, h }
}

/** The tile a dragged window would take with the pointer at (px, py), or null. */
export function edgeTarget(px: number, py: number, area: Rect): TileKind | null {
  const nearL = px <= area.x + EDGE
  const nearR = px >= area.x + area.w - EDGE
  const nearT = py <= area.y + EDGE
  const nearB = py >= area.y + area.h - EDGE
  const inL = px <= area.x + CORNER
  const inR = px >= area.x + area.w - CORNER
  const inT = py <= area.y + CORNER
  const inB = py >= area.y + area.h - CORNER
  if ((nearL && inT) || (nearT && inL)) return 'tl'
  if ((nearR && inT) || (nearT && inR)) return 'tr'
  if ((nearL && inB) || (nearB && inL)) return 'bl'
  if ((nearR && inB) || (nearB && inR)) return 'br'
  if (nearL) return 'left'
  if (nearR) return 'right'
  if (nearT) return 'fill'
  return null
}

/** Every visible window side by side: a near-square grid over the work area. */
export function arrange(n: number, area: Rect, gap = GAP): Rect[] {
  if (n <= 0) return []
  if (n === 1) return [tileRect('fill', area, gap)]
  const cols = Math.ceil(Math.sqrt(n))
  const rows = Math.ceil(n / cols)
  const W = area.w - 2 * gap
  const H = area.h - 2 * gap
  const cw = (W - gap * (cols - 1)) / cols
  const ch = (H - gap * (rows - 1)) / rows
  const out: Rect[] = []
  for (let i = 0; i < n; i += 1) {
    const r = Math.floor(i / cols)
    const c = i % cols
    // The last row spreads its windows over the whole width.
    const inRow = r === rows - 1 ? n - cols * (rows - 1) : cols
    const w = r === rows - 1 ? (W - gap * (inRow - 1)) / inRow : cw
    out.push({ x: area.x + gap + c * (w + gap), y: area.y + gap + r * (ch + gap), w, h: ch })
  }
  return out
}

/** Re-place a window after the work area changed (browser resize, Dock moved). */
export function reflow(s: WinState, area: Rect): WinState {
  const rect = s.tile ? tileRect(s.tile, area) : clampRect(s.rect, area)
  return rect.x === s.rect.x && rect.y === s.rect.y && rect.w === s.rect.w && rect.h === s.rect.h
    ? s : { ...s, rect }
}

// ── per-device memory (a viewer convenience, never needed for correctness) ──

type Stored = Record<string, Pick<WinState, 'rect' | 'tile' | 'restore' | 'min'>>

const key = (scope: string) => `twasta:shell:mac:windows:${scope}`

export function loadWindows(scope: string): Stored {
  try {
    const raw = localStorage.getItem(key(scope))
    const doc = raw ? JSON.parse(raw) : {}
    return doc && typeof doc === 'object' ? doc as Stored : {}
  } catch {
    return {}
  }
}

export function saveWindows(scope: string, wins: Record<string, WinState>): void {
  try {
    const out: Stored = {}
    for (const [id, w] of Object.entries(wins)) out[id] = { rect: w.rect, tile: w.tile, restore: w.restore, min: w.min }
    localStorage.setItem(key(scope), JSON.stringify(out))
  } catch {
    /* storage refused: windows just open at their default place next time */
  }
}
